import React, { useState, useEffect, useMemo } from 'react';
import { Table } from 'antd';
import { Tabs } from '@jcm/design-system';
import { TObrigacao } from '../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import { PerfilFuncionario } from '../../../ts/enums/FuncaoEnum';
import { useGovernancaContext } from '../../../../../../context/GovernancaContext';
import AlertComponent from '../AlertComponent/AlertComponent';
import estilo from './index.module.scss';
import { colunasTabela } from '../utils/colunasTabela';
import TabMenuObrigacoes from '../../Obrigacoes/ObrigacoesTabMenu/TabMenuObrigacoes';

interface OperacoesTabelaProps {
	obrigacoes: TObrigacao[];
	empresaId: string | number;
	onObrigacaoChange: (obrigacaoAtualizada: TObrigacao) => void;
	onAtualizar?: () => void;
	onSelectChange: (selecionados: number[]) => void;
	loading: boolean;
}

const OperacoesTabela: React.FC<OperacoesTabelaProps> = ({ obrigacoes, onSelectChange, loading }) => {
	const { usuario } = useGovernancaContext();
	const [paginaAtual, setPaginaAtual] = useState(1);
	const [tamanhoPagina, setTamanhoPagina] = useState(10);
	const [chavesSelecionadas, setChavesSelecionadas] = useState<number[]>([]);
	const [filteredData, setFilteredData] = useState<TObrigacao[]>(obrigacoes);
	const [exibirAlertaAmarelo, setExibirAlertaAmarelo] = useState(false);
	const [mostrarSelecionados, setMostrarSelecionados] = useState(false);
	const [activeTabKey, setActiveTabKey] = useState('todas');
	const [filterText, setFilterText] = useState<{ [key: string]: any }>({
		codigo: '',
		nome: '',
		responsavel: '',
		aprovador: '',
		dataVencimento: [],
	});

	useEffect(() => {
		const filtered = obrigacoes.filter((item) => {
			const isResponsavel = item.responsaveis.some(
				(r) => r.principal && r.funcionario.funcionarioId === usuario?.funcionarioId,
			);

			const podeVer =
				usuario?.funcao === PerfilFuncionario.AdministradorEmpresa ||
				usuario?.funcao === PerfilFuncionario.AdministradorSistema ||
				isResponsavel;

			return podeVer;
		});

		setFilteredData(filtered);
	}, [obrigacoes, usuario]);

	useEffect(() => {
		setChavesSelecionadas([]);
		onSelectChange([]);
	}, [obrigacoes]);

	useEffect(() => {
		if (chavesSelecionadas.length === 0) {
			setExibirAlertaAmarelo(false);
		}
	}, [chavesSelecionadas]);

	const dataParaExibir = mostrarSelecionados
		? filteredData.filter((item) => chavesSelecionadas.includes(item.id))
		: filteredData;

	const colunas = colunasTabela(obrigacoes, filterText, setFilterText, estilo);

	const paginas = useMemo(
		() => [
			{
				key: 'todas',
				label: (
					<TabMenuObrigacoes
						tituloPagina='Todas Obrigações'
						rotuloContagemObrigações='todos'
						quantidade={filteredData.length}
					/>
				),
			},
			{
				key: 'selecionadas',
				label: (
					<TabMenuObrigacoes
						tituloPagina='Selecionadas'
						rotuloContagemObrigações='selecionados'
						quantidade={chavesSelecionadas.length}
					/>
				),
			},
		],
		[filteredData, chavesSelecionadas],
	);

	const handleTabChange = (key: string) => {
		setActiveTabKey(key);
		setMostrarSelecionados(key === 'selecionadas');
	};

	return (
		<div className={estilo.tableContainer}>
			<div className={estilo.counterContainer}>
				<Tabs items={paginas} activeKey={activeTabKey} onChange={handleTabChange} />
			</div>
			{exibirAlertaAmarelo && (
				<AlertComponent
					message={`Todas as ${chavesSelecionadas.length} obrigações foram selecionadas.`}
					onClose={() => setExibirAlertaAmarelo(false)}
				/>
			)}
			<Table
				loading={loading}
				rowSelection={{
					selectedRowKeys: chavesSelecionadas,
					onChange: (chaves) => {
						const idsSelecionados = chaves.map((key) => Number(key));
						setChavesSelecionadas(idsSelecionados);
						onSelectChange(idsSelecionados);
					},
					hideSelectAll: true,
				}}
				columns={colunas}
				dataSource={dataParaExibir}
				pagination={{
					position: ['bottomCenter'],
					pageSize: tamanhoPagina,
					current: paginaAtual,
					onChange: (pagina) => setPaginaAtual(pagina),
					onShowSizeChange: (_, tamanho) => setTamanhoPagina(tamanho),
				}}
				rowKey='id'
				expandable={{ showExpandColumn: false }}
			/>
		</div>
	);
};

export default OperacoesTabela;
